import React, { useState, useEffect, useCallback } from 'react';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import * as SavedScenarioAPI from '../../../services/SavedScenarioService';
import ScenarioCard from './ScenarioCard';
import styles from './styles/ScenarioCarousel.module.css';
import Loading from '../../loading/Loading';
import SortFilterDropdown from '../../common/SortFilterDropdown';

const ITEMS_PER_PAGE = 20;

// Values are the canonical sort keys the API accepts; labels are display-only.
const SORT_OPTIONS = [
    { value: 'views', label: 'Views' },
    { value: 'title', label: 'Title' },
    { value: 'publishedAt', label: 'Published Date' },
    { value: 'commentsCount', label: 'Comments' },
    { value: 'avgRating', label: 'Rating' },
];

const DEFAULT_CONTROLS = {
    sortBy: '',
    sortOrder: 'desc',
    filterBy: '',
    creator: '',
    tags: [],
};

const ScenarioCarousel = ({ activeTab, activeSubTab, mapSubTabToTag, activeTag, savedOnly = false }) => {
    const [scenariosList, setScenariosList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);

    const [controls, setControls] = useState(DEFAULT_CONTROLS);

    const handleControlsChange = useCallback((partial) => {
        setControls(prev => ({ ...prev, ...partial }));
    }, []);

    const handleSavedChange = (
        scenarioId,
        nextSavedStatus,
        updatedSavedCounts
    ) => {
        setScenariosList((previousScenarios) => {
            if (savedOnly && !nextSavedStatus) {
                return previousScenarios.filter(
                    (scenario) => scenario._id !== scenarioId
                );
            }

            return previousScenarios.map((scenario) =>
                scenario._id === scenarioId
                    ? {
                        ...scenario,
                        isSaved: nextSavedStatus,
                        savedCounts: updatedSavedCounts
                    }
                    : scenario
            );
        });
    };

    const fetchScenarios = useCallback(async (currentPage, isNewTab) => {
        if (isNewTab) {
            setIsLoading(true);
            setScenariosList([]);
            setError(null);
        } else {
            setIsLoadingMore(true);
        }

        let filterType = '';
        let tag = '';

        if (!savedOnly) {
            if (controls.tags.length > 0) {
                filterType = 'tag';
                tag = controls.tags.map(t => t.name).join(',');
            } else {
                const tabKey = activeTab.toLowerCase().replace(/\s+/g, '');
                switch (tabKey) {
                    case 'featured':
                        filterType = 'featured';
                        break;
                    case 'mostviewed':
                        filterType = 'mostViewed';
                        break;
                    case 'new':
                        filterType = 'new';
                        break;
                    case 'recentlyupdated':
                        filterType = 'recentlyUpdated';
                        break;
                    case 'education':
                        filterType = 'tag';
                        tag = activeSubTab ? mapSubTabToTag(activeSubTab) : 'education';
                        break;
                    case 'entertainment':
                        filterType = 'tag';
                        tag = activeSubTab ? mapSubTabToTag(activeSubTab) : 'entertainment';
                        break;
                    default:
                        console.warn('Unknown tab:', activeTab);
                        filterType = 'featured';
                }
            }
        }

        const params = {
            filterType,
            page: currentPage,
            limit: ITEMS_PER_PAGE,
        };

        if (!savedOnly) {
            if (tag) params.tag = tag;
            if (controls.filterBy) params.filterBy = controls.filterBy;
            if (controls.creator) params.creator = controls.creator;
            if (controls.sortBy) {
                params.sortBy = controls.sortBy;
                params.sortOrder = controls.sortOrder;
            }
        }

        try {
            const data = savedOnly
                ? await SavedScenarioAPI.getSavedScenarios(params)
                : await ScenarioAPI.getHomepageScenarios(params);
            const { scenarios, hasMore: newHasMore } = data;

            if (isNewTab) {
                setScenariosList(scenarios);
            } else {
                setScenariosList(prevScenarios => [...prevScenarios, ...scenarios]);
            }
            setHasMore(newHasMore);
            setPage(currentPage);

        } catch (err) {
            console.error(`Error fetching ${filterType} scenarios:`, err);
            setError(`Failed to load scenarios. ${err.response?.data?.message || err.message}`);
            setScenariosList([]);
        } finally {
            if (isNewTab) setIsLoading(false);
            else setIsLoadingMore(false);
        }
    }, [activeTab, activeSubTab, mapSubTabToTag, controls, savedOnly]);

    // Any change to the tab or the sort/filter controls triggers a fresh load
    // (fetchScenarios is recreated whenever its inputs change).
    useEffect(() => {
        setPage(1);
        fetchScenarios(1, true);
    }, [fetchScenarios]);

    const handleLoadMore = () => {
        if (!isLoadingMore && hasMore) {
            fetchScenarios(page + 1, false);
        }
    };

    if (isLoading) {
        return <div className={styles.centeredContainer}><Loading /></div>;
    }

    if (error) {
        return <div className={styles.errorState}>Error: {error}</div>;
    }

    return (
        <>
            {/* The saved-scenarios endpoint doesn't support sort/filter params yet,
                so the controls only render on the browse tabs. */}
            {!savedOnly && (
                <div className={styles.controlsBar}>
                    <SortFilterDropdown
                        sortOptions={SORT_OPTIONS}
                        value={controls}
                        onChange={handleControlsChange}
                        showAccessFilter
                        showCreatorSearch
                        showTagSearch
                    />
                </div>
            )}

            {scenariosList.length === 0 ? (
                <div className={styles.emptyState}>
                    {savedOnly
                    ? 'You have not saved any scenarios yet.'
                    : 'No scenarios found for this category.'
                    }
                </div>
            ) : (
                <div className={styles.gridContainer}>
                    {scenariosList.map((scenario) => (
                        <ScenarioCard key={scenario._id} scenario={scenario} onSavedChange={handleSavedChange} />
                    ))}
                </div>
            )}

            {isLoadingMore && (
                <div className={styles.loadingIndicatorContainer}>
                    <Loading small />
                </div>
            )}
            {hasMore && !isLoadingMore && (
                <div className={styles.loadMoreActionContainer}>
                    <button onClick={handleLoadMore} className={styles.loadMoreButton}>
                        Load More Scenarios
                    </button>
                </div>
            )}
        </>
    );
};

export default ScenarioCarousel;
